/**
 * Fisher's exact test for a 2 × 2 table
 *
 *            col 1   col 2
 *   row 1      a       b
 *   row 2      c       d
 *
 * With every margin fixed, a follows the hypergeometric distribution: N = a + b + c + d
 * items, a + c of them "successes", a + b drawn. The p-values match
 * `scipy.stats.fisher_exact` and R's `fisher.test`:
 *
 * - two-sided: the total probability of every table no more likely than the observed
 *   one, with R's relative tolerance of 1 + 10⁻⁷ so that tables equal in exact
 *   arithmetic are not split by floating-point noise;
 * - less: P(X ≤ a); greater: P(X ≥ a).
 *
 * Two odds ratios, because the two packages disagree and readers compare them:
 * SciPy reports the sample odds ratio ad / bc; R reports the conditional maximum-
 * likelihood estimate under the noncentral hypergeometric distribution, with an exact
 * confidence interval obtained by inverting the two one-sided tests
 * (`scipy.stats.contingency.odds_ratio(kind="conditional")` gives the same).
 */

import { hypergeometricPmf } from "@/lib/stats/distributions";
import { logChoose } from "@/lib/stats/special";

export interface FisherResult {
  table: [number, number, number, number];
  n: number;
  /** The support of a given the margins. */
  min: number;
  max: number;
  /** P(X = a) — the probability of the observed table. */
  pObserved: number;
  pTwoSided: number;
  pLess: number;
  pGreater: number;
  /** ad / bc; Infinity when bc = 0 < ad. */
  sampleOddsRatio: number;
  /** R's estimate: 0 or Infinity at the edges of the support. */
  conditionalMle: number;
  ciLow: number;
  ciHigh: number;
  level: number;
  /** P(X = k) for every k in the support, for the distribution chart. */
  distribution: { k: number; p: number }[];
}

const RELATIVE_TOLERANCE = 1 + 1e-7;

/** log of the noncentral hypergeometric weights C(K, k)·C(N − K, n − k)·ψᵏ, normalised. */
function noncentralProbabilities(
  min: number,
  logBase: number[],
  logPsi: number
): number[] {
  const logs = logBase.map((value, i) => value + (min + i) * logPsi);
  const top = Math.max(...logs);
  const weights = logs.map((value) => Math.exp(value - top));
  const total = weights.reduce((acc, w) => acc + w, 0);
  return weights.map((w) => w / total);
}

/** Solve an increasing function of log ψ for a target by bracketing then bisection. */
function solveLogPsi(f: (logPsi: number) => number, target: number): number {
  let lo = -1;
  let hi = 1;
  while (f(lo) > target && lo > -700) {
    lo *= 2;
  }
  while (f(hi) < target && hi < 700) {
    hi *= 2;
  }
  for (let i = 0; i < 200; i++) {
    const mid = (lo + hi) / 2;
    if (f(mid) < target) {
      lo = mid;
    } else {
      hi = mid;
    }
    if (hi - lo < 1e-13) {
      break;
    }
  }
  return (lo + hi) / 2;
}

function conditionalEstimates(
  a: number,
  min: number,
  max: number,
  logBase: number[],
  level: number
): { mle: number; low: number; high: number } {
  const probs = (logPsi: number) =>
    noncentralProbabilities(min, logBase, logPsi);
  const meanAt = (logPsi: number) =>
    probs(logPsi).reduce((acc, p, i) => acc + p * (min + i), 0);
  const upperTail = (logPsi: number) =>
    probs(logPsi).reduce((acc, p, i) => (min + i >= a ? acc + p : acc), 0);
  const lowerTail = (logPsi: number) =>
    probs(logPsi).reduce((acc, p, i) => (min + i <= a ? acc + p : acc), 0);
  const alpha = (1 - level) / 2;
  let mle = 0;
  if (a === max) {
    mle = Number.POSITIVE_INFINITY;
  } else if (a > min) {
    mle = Math.exp(solveLogPsi(meanAt, a));
  }
  // P(X ≥ a; ψ) rises with ψ; P(X ≤ a; ψ) falls, so solve its negative.
  const low = a === min ? 0 : Math.exp(solveLogPsi(upperTail, alpha));
  const high =
    a === max
      ? Number.POSITIVE_INFINITY
      : Math.exp(solveLogPsi((x) => -lowerTail(x), -alpha));
  return { mle, low, high };
}

export function fisherExact(
  a: number,
  b: number,
  c: number,
  d: number,
  level = 0.95
): FisherResult | null {
  const cells = [a, b, c, d];
  if (cells.some((value) => !Number.isInteger(value) || value < 0)) {
    return null;
  }
  const n = a + b + c + d;
  const row1 = a + b;
  const col1 = a + c;
  if (row1 === 0 || col1 === 0 || row1 === n || col1 === n) {
    return null;
  }
  const min = Math.max(0, row1 - (n - col1));
  const max = Math.min(row1, col1);
  const distribution: { k: number; p: number }[] = [];
  for (let k = min; k <= max; k++) {
    distribution.push({ k, p: hypergeometricPmf(k, n, col1, row1) });
  }
  const pObserved = hypergeometricPmf(a, n, col1, row1);
  let pTwoSided = 0;
  let pLess = 0;
  let pGreater = 0;
  for (const { k, p } of distribution) {
    if (p <= pObserved * RELATIVE_TOLERANCE) {
      pTwoSided += p;
    }
    if (k <= a) {
      pLess += p;
    }
    if (k >= a) {
      pGreater += p;
    }
  }
  const logBase = distribution.map(
    ({ k }) => logChoose(col1, k) + logChoose(n - col1, row1 - k)
  );
  const estimates = conditionalEstimates(a, min, max, logBase, level);
  let sampleOddsRatio = Number.NaN;
  if (b * c > 0) {
    sampleOddsRatio = (a * d) / (b * c);
  } else if (a * d > 0) {
    sampleOddsRatio = Number.POSITIVE_INFINITY;
  }
  return {
    table: [a, b, c, d],
    n,
    min,
    max,
    pObserved,
    pTwoSided: Math.min(1, pTwoSided),
    pLess: Math.min(1, pLess),
    pGreater: Math.min(1, pGreater),
    sampleOddsRatio,
    conditionalMle: estimates.mle,
    ciLow: estimates.low,
    ciHigh: estimates.high,
    level,
    distribution,
  };
}
